import _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

import { getAuthUser } from '../../../lib/services/auth.service';
import GlobalAgeGroupFilterContainer from './global-age-group-filter.container';
import { buildUpdateUserSettings } from '../../../lib/services/user.service';
import { useRoleAllowedAgeGroups } from '../../../lib/services/age-group.service';

const GET_GLOBAL_AGE_GROUP_FILTER = gql`
  query GetGlobalAgeGroupFilter($assessmentId: ID!) {
    ageGroups(assessmentId: $assessmentId) {
      _id
      name
      assessmentId
    }
  }
`;

export const GlobalAgeGroupFilterGraphQL = ({requiredRoles}: {requiredRoles?: string[]}) => {
  const user = getAuthUser();
  const { data, error, loading } = useQuery(GET_GLOBAL_AGE_GROUP_FILTER, {
    fetchPolicy: 'cache-and-network',
    skip: !user || !user.settings.activeAssessmentId,
    variables: { assessmentId: user && user.settings.activeAssessmentId }
  });

  const { updateUserSettings } = buildUpdateUserSettings();

  const ageGroups = useMemo(() => ((data && data.ageGroups) || []) as IAgeGroup[], [data])
  const availableAgeGroupsForRoles = useRoleAllowedAgeGroups({ageGroups, requiredRoles})

  const userActiveAgeGroupIds = useMemo(() => {
    const newActiveAgeGroups = { belongsToAssessment: [] as string[], unrelated: [] as string[] }
    if (user?.settings.activeAgeGroupIds) {
      user.settings.activeAgeGroupIds.forEach((ag: string) => {
        if (availableAgeGroupsForRoles.find(a => a._id === ag)) {
          newActiveAgeGroups.belongsToAssessment.push(ag)
        } else {
          newActiveAgeGroups.unrelated.push(ag)
        }
      })
    }
    return newActiveAgeGroups
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableAgeGroupsForRoles, user?.settings.activeAgeGroupIds]);

  const changeActiveAgeGroups = useCallback((ageGroupIds: string[], options?: { userInitiated?: boolean }) =>
    updateUserSettings({ activeAgeGroupIds: _.uniq([...ageGroupIds, ...userActiveAgeGroupIds.unrelated]) }, options),
  [updateUserSettings, userActiveAgeGroupIds.unrelated]);

  useEffect(() => {
    // Calculate difference between active age groups with allowed age groups for roles, and then auto update users active age groups
    if (user?.settings.activeAgeGroupIds) {
      const intersection = _.intersection(userActiveAgeGroupIds.belongsToAssessment, availableAgeGroupsForRoles.map(ag => ag._id))
      if (intersection.length !== userActiveAgeGroupIds.belongsToAssessment.length) {
        changeActiveAgeGroups(intersection, { userInitiated: false });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableAgeGroupsForRoles, changeActiveAgeGroups, user?.settings.activeAgeGroupIds, userActiveAgeGroupIds])

  return <GlobalAgeGroupFilterContainer
    activeAgeGroupIds={user?.settings.activeAgeGroupIds || []}
    changeActiveAgeGroups={changeActiveAgeGroups}
    fetchError={Boolean(error)}
    loading={loading}
    ageGroups={availableAgeGroupsForRoles}
  />;
};
